import { AlertTriangle } from 'lucide-react'
import type { Permission } from '../lib/api'
import {
  groupCatalog,
  levelFor,
  LEVEL_LABELS,
  type CategoryGroup,
  type Level,
} from '../lib/rolePermissions'
import { InfoTooltip } from './ui/InfoTooltip'

interface RolePermissionPickerProps {
  catalog: Permission[]
  value: string[]
  onChange: (keys: string[]) => void
  readOnly?: boolean
}

export function RolePermissionPicker({ catalog, value, onChange, readOnly = false }: RolePermissionPickerProps) {
  const groups = groupCatalog(catalog)

  function setLevel(group: CategoryGroup, level: Level) {
    if (readOnly) return
    const next = new Set(value)
    // Clear this category's keys, then re-add per the chosen level.
    if (group.readKey) next.delete(group.readKey)
    if (group.writeKey) next.delete(group.writeKey)
    if (level === 'read' && group.readKey) {
      next.add(group.readKey)
    } else if (level === 'readwrite') {
      if (group.readKey) next.add(group.readKey)
      if (group.writeKey) next.add(group.writeKey)
    }
    onChange(Array.from(next))
  }

  const levels: { id: Level; label: string }[] = [
    { id: 'none', label: LEVEL_LABELS.none },
    { id: 'read', label: LEVEL_LABELS.read },
    { id: 'readwrite', label: LEVEL_LABELS.readwrite },
  ]

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const current = levelFor(group, value)
        return (
          <div
            key={group.category}
            className="flex flex-col gap-2 py-2 border-b border-border last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{group.category}</span>
                <InfoTooltip
                  label={`What does ${group.category} grant?`}
                  content={
                    <div className="space-y-1.5">
                      {group.sensitive && (
                        <p className="flex items-center gap-1 font-medium text-amber-600">
                          <AlertTriangle className="w-3 h-3" /> Sensitive permission
                        </p>
                      )}
                      {group.readDescription && (
                        <p>
                          <span className="font-medium">Read:</span> {group.readDescription}
                        </p>
                      )}
                      {group.writeDescription && (
                        <p>
                          <span className="font-medium">Read &amp; write:</span> {group.writeDescription}
                        </p>
                      )}
                    </div>
                  }
                />
                {group.sensitive && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-500/15 text-amber-600">
                    <AlertTriangle className="w-3 h-3" /> Sensitive
                  </span>
                )}
              </div>
              {group.description && (
                <p className="text-sm text-muted-foreground sm:truncate">{group.description}</p>
              )}
            </div>
            <div className="flex flex-shrink-0 self-start rounded-md overflow-hidden border border-border sm:self-auto">
              {levels.map((lvl) => {
                const active = current === lvl.id
                return (
                  <button
                    key={lvl.id}
                    type="button"
                    disabled={readOnly}
                    onClick={() => setLevel(group, lvl.id)}
                    className={`px-3 py-1 text-sm transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    } ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {lvl.label}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default RolePermissionPicker
